/**
 * CFO Monthly Pack — Iter-102
 *
 * Executive snapshot UI:
 *   • Month picker (default = current month)
 *   • Live on-screen preview (6 sections: KPIs / P&L / AR / Cash / Compliance / Ops)
 *   • Download PDF · Email (mock until provider configured) · History list
 */
import React, { useEffect, useState } from "react";
import { axiosInstance } from "../App";
import { toast } from "sonner";
import {
  Briefcase, TrendingUp, Wallet, AlertCircle, FileDown, Mail,
  Loader2, Send, History, FileText, ShieldCheck, Activity,
  ArrowDownRight, ArrowUpRight, Calendar,
} from "lucide-react";

const SAR = (n) => new Intl.NumberFormat("en-US", {
  style: "currency", currency: "SAR", maximumFractionDigits: 0,
}).format(Number(n || 0));

const PCT = (n) => `${Number(n || 0).toFixed(1)}%`;

const currentMonth = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
};

// ─── Mini components ─────────────────────────────────────────────

const KPI = ({ label, value, icon: Icon, color = "violet", subtle, tone }) => (
  <div data-testid={`pack-kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}
       className={`rounded-xl border border-${color}-200 bg-white p-4 shadow-sm`}>
    <div className="flex items-center justify-between">
      <div className={`flex h-9 w-9 items-center justify-center rounded-lg bg-${color}-100 text-${color}-700`}>
        <Icon className="h-4 w-4" />
      </div>
      {tone && <span className={`text-xs font-bold ${tone}`}>{tone.includes("emerald") ? "↑" : "↓"}</span>}
    </div>
    <div className="mt-2.5 text-[11px] font-medium uppercase tracking-wide text-slate-500">{label}</div>
    <div className="mt-0.5 text-lg font-bold text-slate-900">{value}</div>
    {subtle && <div className="mt-0.5 text-xs text-slate-500">{subtle}</div>}
  </div>
);

const SectionCard = ({ title, icon: Icon, children, action }) => (
  <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
    <div className="mb-4 flex items-center justify-between">
      <h3 className="flex items-center gap-2 text-sm font-bold text-slate-900">
        <Icon className="h-4 w-4 text-violet-600" />{title}
      </h3>
      {action}
    </div>
    {children}
  </div>
);

// ─── Email dialog ────────────────────────────────────────────────

const EmailDialog = ({ month, onClose }) => {
  const [recipients, setRecipients] = useState("");
  const [sending, setSending] = useState(false);
  const submit = async () => {
    const list = recipients.split(/[,\s]+/).map(s => s.trim()).filter(Boolean);
    if (list.length === 0) { toast.error("أدخل بريداً واحداً على الأقل"); return; }
    setSending(true);
    try {
      const r = await axiosInstance.post("/cfo-pack/email", {
        month, recipients: list,
      });
      if (r.data?.status === "MOCKED") {
        toast.message("تم تسجيل الطلب في سجل التدقيق",
          { description: "مزود البريد (Resend/SendGrid) غير مُكوَّن بعد. الطلب مُسجَّل في Audit Trail." });
      } else {
        toast.success("تم إرسال التقرير");
      }
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل إرسال التقرير");
    } finally { setSending(false); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
      <div data-testid="pack-email-dialog"
            className="w-full max-w-md rounded-2xl bg-white p-5 shadow-xl">
        <h3 className="text-lg font-bold text-slate-900">إرسال CFO Pack بالبريد</h3>
        <p className="mt-1 text-xs text-amber-700 bg-amber-50 rounded-lg p-2">
          ⚠ مزود البريد لم يُكوَّن بعد — الطلب سيُسجَّل في Audit Trail (mock).
        </p>
        <div className="mt-4 space-y-2">
          <label className="text-xs font-medium text-slate-600">المستلمون (مفصولين بفاصلة)</label>
          <textarea
            data-testid="pack-email-recipients"
            value={recipients}
            onChange={(e) => setRecipients(e.target.value)}
            rows={3}
            dir="ltr"
            placeholder="cfo@example.com, board@example.com"
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-mono"
          />
        </div>
        <div className="mt-5 flex items-center justify-end gap-2">
          <button onClick={onClose}
                   className="rounded-lg border border-slate-300 px-3 py-2 text-sm">
            إلغاء
          </button>
          <button data-testid="pack-email-send" disabled={sending} onClick={submit}
                   className="inline-flex items-center gap-1.5 rounded-lg bg-violet-600 px-4 py-2 text-sm font-semibold text-white hover:bg-violet-700 disabled:opacity-60">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            إرسال
          </button>
        </div>
      </div>
    </div>
  );
};

// ─── Main page ───────────────────────────────────────────────────

export default function CFOPack() {
  const [month, setMonth] = useState(currentMonth());
  const [pack, setPack] = useState(null);
  const [loading, setLoading] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [history, setHistory] = useState([]);

  const loadPreview = async (m) => {
    setLoading(true); setPack(null);
    try {
      const r = await axiosInstance.get(`/cfo-pack/preview?month=${m}`);
      setPack(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل تحميل المعاينة");
    } finally { setLoading(false); }
  };

  const loadHistory = async () => {
    try {
      const r = await axiosInstance.get("/cfo-pack/history?limit=20");
      setHistory(r.data?.history || []);
    } catch { /* silent */ }
  };

  useEffect(() => { loadPreview(month); /* eslint-disable-next-line */ }, [month]);
  useEffect(() => { loadHistory(); }, []);

  const downloadPDF = async () => {
    setDownloading(true);
    try {
      const res = await axiosInstance.get(`/cfo-pack/pdf?month=${month}`,
                                                  { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/pdf" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `CFO_Monthly_Pack_${month}.pdf`;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(a.href), 2000);
      toast.success("تم تحميل التقرير الشهري");
      loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "فشل تحميل PDF");
    } finally { setDownloading(false); }
  };

  const ex = pack?.executive;

  return (
    <div data-testid="cfo-pack-page" className="space-y-6 p-6" dir="rtl">
      {/* Header */}
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">
            <Briefcase className="inline -mt-1 ml-2 h-7 w-7 text-violet-600" />
            CFO Monthly Pack
          </h1>
          <p className="mt-1 text-sm text-slate-500">
            تقرير تنفيذي شهري — مالي · ذمم · نقدية · امتثال · عمليات
          </p>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex items-center gap-2 rounded-xl border border-slate-300 bg-white px-3 py-2">
            <Calendar className="h-4 w-4 text-slate-400" />
            <input
              data-testid="pack-month-picker"
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="border-0 bg-transparent text-sm font-mono outline-none"
            />
          </div>
          <button
            data-testid="pack-download-pdf"
            disabled={downloading || !pack}
            onClick={downloadPDF}
            className="inline-flex items-center gap-1.5 rounded-lg border border-violet-500 bg-violet-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-violet-700 disabled:opacity-60"
          >
            {downloading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileDown className="h-4 w-4" />}
            تنزيل PDF
          </button>
          <button
            data-testid="pack-open-email"
            disabled={!pack}
            onClick={() => setEmailOpen(true)}
            className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-500 bg-emerald-600 px-4 py-2 text-sm font-semibold text-white shadow-sm hover:bg-emerald-700 disabled:opacity-60"
          >
            <Mail className="h-4 w-4" />
            إرسال بريد
          </button>
        </div>
      </div>

      {loading && (
        <div className="flex items-center justify-center rounded-2xl border border-slate-200 bg-white py-16 text-slate-500">
          <Loader2 className="mr-2 h-5 w-5 animate-spin" /> جارٍ بناء المعاينة…
        </div>
      )}

      {!loading && pack && (
        <div className="space-y-5">
          {/* Executive KPI grid */}
          <div className="grid gap-3 md:grid-cols-4">
            <KPI label="مشاريع نشطة"      value={ex.active_projects}  icon={Briefcase} color="violet" />
            <KPI label="إيرادات (إجمالي)" value={SAR(ex.total_revenue)} icon={TrendingUp} color="emerald" />
            <KPI label="ربح إجمالي"       value={SAR(ex.total_gp)}
                  icon={Activity} color={ex.total_gp >= 0 ? "emerald" : "rose"} />
            <KPI label="هامش عام"        value={PCT(ex.overall_margin)}
                  icon={Activity} color={ex.overall_margin >= 0 ? "emerald" : "rose"} />
            <KPI label="ذمم مفتوحة"      value={SAR(ex.total_ar)}     icon={Wallet} color="indigo" />
            <KPI label="+90 يوم"          value={SAR(ex.ar_90_plus)}   icon={AlertCircle} color="rose" />
            <KPI label="عملاء عالي الخطر" value={ex.at_risk_customers} icon={AlertCircle} color="amber" />
            <KPI label="VOs (الشهر)"      value={ex.vos_approved}      icon={FileText} color="sky" />
          </div>

          {/* Section grid */}
          <div className="grid gap-5 lg:grid-cols-2">
            {/* P&L by Project */}
            <SectionCard title="ملخص P&L حسب المشروع" icon={TrendingUp}
                          action={<span className="text-xs text-slate-500">{pack.pnl.rows.length} مشروع</span>}>
              <div className="overflow-x-auto" style={{ maxHeight: "320px" }}>
                <table data-testid="pack-pnl-table" className="w-full text-xs">
                  <thead className="bg-slate-50 text-slate-600 sticky top-0">
                    <tr>
                      <th className="px-3 py-2 text-right">المشروع</th>
                      <th className="px-3 py-2 text-right">الإيراد</th>
                      <th className="px-3 py-2 text-right">GP</th>
                      <th className="px-3 py-2 text-center">الهامش</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {pack.pnl.rows.slice(0, 10).map((r, i) => (
                      <tr key={i} className="hover:bg-violet-50/50">
                        <td className="px-3 py-2 truncate max-w-[160px]" title={r.project}>{r.project}</td>
                        <td className="px-3 py-2 font-mono">{SAR(r.revenue)}</td>
                        <td className={`px-3 py-2 font-mono ${r.gp >= 0 ? "text-emerald-700" : "text-rose-700"}`}>{SAR(r.gp)}</td>
                        <td className="px-3 py-2 text-center font-mono">{PCT(r.margin)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </SectionCard>

            {/* AR Health */}
            <SectionCard title="صحة الذمم المدينة" icon={Wallet}>
              <div className="grid grid-cols-2 gap-2">
                <div className="rounded-lg bg-emerald-50 p-3">
                  <div className="text-[10px] uppercase text-emerald-700">حالية</div>
                  <div className="text-base font-bold text-emerald-900">{SAR(pack.ar.current)}</div>
                </div>
                <div className="rounded-lg bg-amber-50 p-3">
                  <div className="text-[10px] uppercase text-amber-700">31-60</div>
                  <div className="text-base font-bold text-amber-900">{SAR(pack.ar["31-60"])}</div>
                </div>
                <div className="rounded-lg bg-orange-50 p-3">
                  <div className="text-[10px] uppercase text-orange-700">61-90</div>
                  <div className="text-base font-bold text-orange-900">{SAR(pack.ar["61-90"])}</div>
                </div>
                <div className="rounded-lg bg-rose-50 p-3">
                  <div className="text-[10px] uppercase text-rose-700">+90</div>
                  <div className="text-base font-bold text-rose-900">{SAR(pack.ar["90+"])}</div>
                </div>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-2 text-xs text-slate-600">
                <div><strong>إجمالي مفتوح:</strong> {SAR(pack.ar.total_open)}</div>
                <div><strong>عدد الفواتير:</strong> {pack.ar.open_count}</div>
              </div>
            </SectionCard>

            {/* Cash Position */}
            <SectionCard title="المركز النقدي (90 يوم)" icon={ArrowDownRight}>
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs text-slate-500">المتوقع داخل</div>
                  <div className="mt-1 text-2xl font-bold text-emerald-700">{SAR(pack.cash.projected_90d_in)}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs text-slate-500">الأفق</div>
                  <div className="mt-1 text-2xl font-bold text-slate-900">{pack.cash.horizon_days} يوم</div>
                </div>
              </div>
            </SectionCard>

            {/* Compliance & Ops */}
            <SectionCard title="الامتثال والعمليات" icon={ShieldCheck}>
              <div className="grid grid-cols-2 gap-2 text-sm">
                <div className="rounded-lg bg-slate-50 p-3">
                  <div className="text-[10px] uppercase text-slate-500">VAT — إقرارات</div>
                  <div className="mt-1 font-bold text-slate-900">{pack.compliance.vat_returns_total}</div>
                </div>
                <div className="rounded-lg bg-rose-50 p-3">
                  <div className="text-[10px] uppercase text-rose-700">VAT — مغلقة</div>
                  <div className="mt-1 font-bold text-rose-900">{pack.compliance.vat_returns_locked}</div>
                </div>
                <div className="rounded-lg bg-violet-50 p-3">
                  <div className="text-[10px] uppercase text-violet-700">أحداث تدقيق</div>
                  <div className="mt-1 font-bold text-violet-900">{pack.compliance.audit_events_month}</div>
                </div>
                <div className="rounded-lg bg-sky-50 p-3">
                  <div className="text-[10px] uppercase text-sky-700">VOs معتمدة</div>
                  <div className="mt-1 font-bold text-sky-900">{pack.operations.vos_approved}</div>
                </div>
                <div className="rounded-lg bg-emerald-50 p-3 col-span-2">
                  <div className="text-[10px] uppercase text-emerald-700">مشاريع جديدة (الشهر)</div>
                  <div className="mt-1 font-bold text-emerald-900">{pack.operations.new_projects}</div>
                </div>
              </div>
            </SectionCard>
          </div>

          {/* History */}
          <SectionCard title="سجل التقارير المُنشأة" icon={History}>
            {history.length === 0 ? (
              <div className="py-6 text-center text-sm text-slate-400">
                لم يتم إنشاء أي تقرير بعد. اضغط &quot;تنزيل PDF&quot; لإنشاء أول تقرير.
              </div>
            ) : (
              <ul data-testid="pack-history-list" className="divide-y divide-slate-100">
                {history.map((h) => (
                  <li key={h.id} className="flex items-center justify-between py-2 text-sm">
                    <div className="flex items-center gap-2">
                      <FileText className="h-4 w-4 text-violet-600" />
                      <span className="font-mono font-bold">{h.month}</span>
                      <span className="text-xs text-slate-500">· بواسطة {h.issued_by_name}</span>
                    </div>
                    <span dir="ltr" className="font-mono text-xs text-slate-500">
                      {String(h.issued_at).slice(0, 19).replace("T", " ")}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </SectionCard>
        </div>
      )}

      {emailOpen && <EmailDialog month={month} onClose={() => setEmailOpen(false)} />}
    </div>
  );
}
